(ns frontend.view.settings2.security.ai-admin.event
  "Events for the AI services admin panel: load config, and create/update/delete
   services, set the tenant default, and edit per-task-type routing."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [fx.ajax :as ajax]
   [frontend.actions.attachment :as attachment-actions]
   [frontend.actions.common :as common-actions]
   [frontend.logging :as logging]
   [frontend.view.settings2.security.ai-admin.model :as model]
   [oops.core :as oops]
   [re-frame.core :as rf]))

(defn- error-message
  [err fallback]
  (or (:message err)
      (get-in err [:response :message])
      fallback))

(defn- path [& ks]
  (into model/root-path ks))

(defn- stored-verifications
  "Badges for what the server already recorded, so health is visible on load
   rather than only after a check in this session."
  [services]
  (->> services
       (keep (fn [{:keys [id model last-verification-outcome last-verified-on]}]
               (when last-verification-outcome
                 ;; The model is the service's CURRENT one rather than a record
                 ;; of what was verified, which is what makes the outcome copy
                 ;; read correctly: saving a changed model re-verifies it, so a
                 ;; stored outcome and the model shown alongside it stay in
                 ;; step. Without this the tooltip interpolates an empty name.
                 [id {:outcome last-verification-outcome
                      :verified-on last-verified-on
                      :model model}])))
       (into {})))

(defn- task-config-drafts
  [task-configs]
  (->> task-configs
       (map (fn [{:keys [task-type internal-email-domains] :as config}]
              [task-type
               (assoc config
                      :internal-email-domains
                      (string/join ", " internal-email-domains))]))
       (into {})))

(defn- task-config-override-drafts
  [overrides]
  (->> overrides
       (map (fn [{:keys [task-type pipeline-id] :as override}]
              [[task-type pipeline-id] override]))
       (into {})))

(defn- parse-email-domains
  [domains]
  (if (string/blank? domains)
    []
    (->> (string/split domains #"[,\s]+")
         (remove string/blank?)
         vec)))

(defn- template-draft-path
  [{:keys [scope task-type pipeline-id]}]
  (if (= :pipeline scope)
    (path :task-config-override-drafts [task-type pipeline-id])
    (path :task-config-drafts task-type)))

(rf/reg-fx
 ::upload-template-fx
 (fn [{:keys [file target-id on-success on-error]}]
   (let [filename (oops/oget file "name")
         upload! (fn [token]
                   (attachment-actions/*=>add-attachment
                    {:multipart-params {:filename filename
                                        :file     file
                                        :target   target-id
                                        :token    token}
                     :progress         (fn [_])}
                    (fn [attachment]
                      (rf/dispatch (conj on-success attachment)))
                    (fn [error]
                      (rf/dispatch (conj on-error error)))))]
     (common-actions/*<-create-op-token
      nil
      upload!
      (fn [error]
        (rf/dispatch (conj on-error error)))))))

(rf/reg-event-fx
 ::init
 (fn [{db :db} _]
   {:db (assoc-in db model/root-path
                  {:loading? true
                   :error    nil
                   :saving?  false
                   :editing  nil})
    :fx [[:dispatch [::load]]]}))

(rf/reg-event-db
 ::cleanup
 (fn [db _]
   (assoc-in db model/root-path nil)))

(rf/reg-event-fx
 ::load
 (fn [{db :db} [_ {:keys [quiet?]}]]
   {:db (cond-> (assoc-in db (path :error) nil)
          (not quiet?) (assoc-in (path :loading?) true))
    ::ajax/send {:operation :ai-settings/load
                 :body      {}
                 :on-ok     [::on-loaded]
                 :on-error  [::on-error]}}))

(rf/reg-event-db
 ::on-loaded
 (fn [db [_ resp]]
   (let [{:keys [services task-configs task-config-overrides deal-pipelines
                 template-upload-target-id default-service-id providers task-types]}
         (:body resp)]
     (-> db
         (assoc-in (path :loading?) false)
         (assoc-in (path :saving?) false)
         (assoc-in (path :services) (or services []))
         (assoc-in (path :task-configs) (or task-configs []))
         (assoc-in (path :task-config-drafts) (task-config-drafts task-configs))
         (assoc-in (path :task-config-overrides) (or task-config-overrides []))
         (assoc-in (path :task-config-override-drafts)
                   (task-config-override-drafts task-config-overrides))
         (assoc-in (path :deal-pipelines) (or deal-pipelines []))
         (assoc-in (path :template-upload-target-id) template-upload-target-id)
         (assoc-in (path :default-service-id) default-service-id)
         (assoc-in (path :providers) (or providers []))
         (assoc-in (path :task-types) (or task-types []))
         ;; Session results win over stored ones. ::on-saved fires a reload and
         ;; a fresh verification together, so the reload can land carrying the
         ;; outcome from BEFORE that save; letting it overwrite would show a
         ;; stale badge whenever it won the race.
         (assoc-in (path :verifications)
                   (merge (stored-verifications services)
                          (get-in db (path :verifications))))))))

(rf/reg-event-db
 ::on-error
 (fn [db [_ err]]
   (logging/error "AI settings load/save failed" err)
   (-> db
       (assoc-in (path :loading?) false)
       (assoc-in (path :saving?) false)
       (assoc-in (path :error) (error-message err (loc/tr :settings/ai-admin-load-error))))))

(defn- clear-test-result
  [db]
  (-> db
      (assoc-in (path :test-result) nil)
      (assoc-in (path :testing?) false)))

(rf/reg-event-db
 ::new-service
 (fn [db _]
   (-> db
       clear-test-result
       (assoc-in (path :editing) model/blank-service))))

(rf/reg-event-db
 ::edit-service
 (fn [db [_ service]]
   ;; api-key is never returned; start blank so leaving it empty keeps the stored key.
   (-> db
       clear-test-result
       (assoc-in (path :editing) (assoc service :api-key "")))))

(rf/reg-event-db
 ::cancel-edit
 (fn [db _]
   (-> db
       clear-test-result
       (assoc-in (path :editing) nil))))

(rf/reg-event-db
 ::update-edit
 (fn [db [_ k v]]
   ;; A verification result describes the fields as they were sent, so editing
   ;; any of them invalidates it - a stale green check is worse than none.
   (-> db
       clear-test-result
       (assoc-in (into (path :editing) [k]) v))))

(rf/reg-event-fx
 ::save-service
 (fn [{db :db} _]
   (let [{:keys [id label provider base-url model api-key enabled?]}
         (get-in db (path :editing))]
     {:db (assoc-in db (path :saving?) true)
      ::ajax/send {:operation :ai-settings/save-service
                   :body      (cond-> {:provider provider
                                       :model    (or model "")
                                       :label    (or label "")
                                       :base-url (or base-url "")
                                       :enabled? (boolean enabled?)}
                                id                       (assoc :id id)
                                (seq (str api-key))      (assoc :api-key api-key))
                   :on-ok     [::on-saved]
                   :on-error  [::on-error]}})))

(defn- on-saved-fx
  "Saving returns as soon as the entity is written, then verification runs on
   its own. It is deliberately not part of the save request: provider calls use
   a 120s socket timeout, so a hanging provider would otherwise leave the admin
   staring at a spinning Save button for two minutes, and a configuration that
   cannot be reached still has to be saveable."
  [{db :db} [_ response]]
  (let [service-id (get-in response [:body :service :id])]
    (cond-> {:db (-> db
                     (assoc-in (path :saving?) false)
                     (assoc-in (path :editing) nil))
             :fx [[:dispatch [::load {:quiet? true}]]]}
      service-id
      (update :fx conj [:dispatch [::verify-service service-id]]))))

(rf/reg-event-fx
 ::on-saved
 on-saved-fx)

(defn- verify-service-fx
  "Verify a stored service as saved. Sending only :id means the backend reads
   provider, base URL, model and key from the entity."
  [{db :db} [_ service-id]]
  {:db (assoc-in db (path :verifications service-id) {:outcome :pending})
   ::ajax/send {:operation :ai-settings/test-service
                :body      {:id service-id}
                :on-ok     [::on-service-verified service-id]
                :on-error  [::on-service-verify-error service-id]}})

(rf/reg-event-fx
 ::verify-service
 verify-service-fx)

(defn- on-service-verified-db
  [db [_ service-id response]]
  (assoc-in db
            (path :verifications service-id)
            (get-in response [:body :result])))

(rf/reg-event-db
 ::on-service-verified
 on-service-verified-db)

(rf/reg-event-db
 ::on-service-verify-error
 (fn [db [_ service-id err]]
   (logging/error "AI service verification failed" err)
   (assoc-in db
             (path :verifications service-id)
             {:outcome :request-failed
              :provider-message (error-message err nil)})))

(defn- test-service-body
  "The draft as submitted for verification. Mirrors ::save-service: the api-key
   is sent only when the admin actually entered one, so a blank field means
   \"verify with the stored key\" rather than \"verify with no key\"."
  [{:keys [id provider base-url model api-key]}]
  (cond-> {:provider provider
           :model    (or model "")
           :base-url (or base-url "")}
    id                  (assoc :id id)
    (seq (str api-key)) (assoc :api-key api-key)))

(defn- test-service-fx
  [{db :db} _]
  {:db (-> db
           (assoc-in (path :testing?) true)
           (assoc-in (path :test-result) nil))
   ::ajax/send {:operation :ai-settings/test-service
                :body      (test-service-body (get-in db (path :editing)))
                :on-ok     [::on-test-result]
                :on-error  [::on-test-error]}})

(rf/reg-event-fx
 ::test-service
 test-service-fx)

(defn- on-test-result-db
  [db [_ response]]
  (-> db
      (assoc-in (path :testing?) false)
      (assoc-in (path :test-result) (get-in response [:body :result]))))

(rf/reg-event-db
 ::on-test-result
 on-test-result-db)

(defn- on-test-error-db
  [db [_ err]]
  (logging/error "AI service verification request failed" err)
  (-> db
      (assoc-in (path :testing?) false)
      (assoc-in (path :test-result)
                {:outcome :request-failed
                 :provider-message (error-message err nil)})))

(rf/reg-event-db
 ::on-test-error
 on-test-error-db)

(rf/reg-event-fx
 ::delete-service
 (fn [{db :db} [_ id]]
   {:db (update-in db (path :verifications) dissoc id)
    ::ajax/send {:operation :ai-settings/delete-service
                 :body      {:id id}
                 :on-ok     [::reloaded]
                 :on-error  [::on-error]}}))

(rf/reg-event-fx
 ::set-default
 (fn [_ [_ service-id]]
   {::ajax/send {:operation :ai-settings/set-default
                 :body      {:service-id service-id}
                 :on-ok     [::reloaded]
                 :on-error  [::on-error]}}))

(rf/reg-event-fx
 ::save-task-config
 (fn [{db :db} [_ task-type]]
   (let [{:keys [service-id token-warning-email style-reference-url
                 document-template-id internal-email-domains]}
         (get-in db (path :task-config-drafts task-type))]
     {:db (assoc-in db (path :saving?) true)
      ::ajax/send {:operation :ai-settings/save-task-config
                   :body      {:task-type               task-type
                               :service-id              service-id
                               :token-warning-email     (or token-warning-email "")
                               :style-reference-url     (or style-reference-url "")
                               :document-template-id    document-template-id
                               :internal-email-domains  (parse-email-domains
                                                         internal-email-domains)}
                   :on-ok     [::reloaded]
                   :on-error  [::on-error]}})))

(rf/reg-event-fx
 ::save-task-config-override
 (fn [{db :db} [_ task-type pipeline-id]]
   (let [{:keys [style-reference-url document-template-id]}
         (get-in db (path :task-config-override-drafts [task-type pipeline-id]))]
     {:db (assoc-in db (path :saving?) true)
      ::ajax/send {:operation :ai-settings/save-task-config-override
                   :body      {:pipeline-id             pipeline-id
                               :task-type               task-type
                               :style-reference-url     (or style-reference-url "")
                               :document-template-id    document-template-id}
                   :on-ok     [::reloaded]
                   :on-error  [::on-error]}})))

(rf/reg-event-db
 ::update-task-config-field
 (fn [db [_ task-type field value]]
   (assoc-in db (path :task-config-drafts task-type field) value)))

(rf/reg-event-db
 ::update-task-config-override-field
 (fn [db [_ task-type pipeline-id field value]]
   (assoc-in db
             (path :task-config-override-drafts [task-type pipeline-id] field)
             value)))

(rf/reg-event-fx
 ::upload-template
 (fn [{db :db} [_ {:keys [file] :as upload}]]
   (let [filename (when file (oops/oget file "name"))
         upload-key (select-keys upload [:scope :task-type :pipeline-id])]
     (if-not (and filename
                  (string/ends-with? (string/lower-case filename) ".docx"))
       {:db (assoc-in db (path :error)
                      (loc/tr :settings/ai-admin-template-file-invalid))}
       {:db (-> db
                (assoc-in (path :error) nil)
                (assoc-in (path :template-uploading upload-key) true))
        ::upload-template-fx
        {:file       file
         :target-id  (get-in db (path :template-upload-target-id))
         :on-success [::template-uploaded upload-key filename]
         :on-error   [::template-upload-failed upload-key]}}))))

(rf/reg-event-db
 ::template-uploaded
 (fn [db [_ upload-key filename attachment]]
   (let [draft-path (template-draft-path upload-key)]
     (-> db
         (assoc-in (conj draft-path :document-template-id) (:db/id attachment))
         (assoc-in (conj draft-path :document-template-filename)
                   (or (:attachment/filename attachment) filename))
         (assoc-in (path :template-uploading upload-key) false)))))

(rf/reg-event-db
 ::template-upload-failed
 (fn [db [_ upload-key error]]
   (logging/error "AI one-pager template upload failed" error)
   (-> db
       (assoc-in (path :template-uploading upload-key) false)
       (assoc-in (path :error)
                 (error-message error
                                (loc/tr :settings/ai-admin-template-upload-error))))))

(rf/reg-event-fx
 ::reloaded
 (fn [_ _]
   {:fx [[:dispatch [::load {:quiet? true}]]]}))
